import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { INPUT_TYPE } from '../../../core/constants/service-inputs/input-type.enum';
import { ServiceInputFormConfig } from '../../../core/constants/service-inputs/service-input-form.config';
import { SERVICE_INPUT_ID } from '../../../core/constants/service-inputs/service-input-id.enum';
import { ServiceInputs } from '../../../core/constants/service-inputs/service-inputs';
import { CustomValidators } from '../../../core/handlers/custom-validators';
import { KeyValue } from '../../../core/models/key-value.model';
import { ServiceBrief } from '../../../core/models/service-brief.mode';
import { ServiceInput } from '../../../core/models/service-input.model';
import { TranslateService } from '../../../core/translate';

@Component({
    selector: 'app-service-input-details',
    templateUrl: './service-input-details.component.html',
    styleUrls: ['./service-input-details.component.css']
})
export class ServiceInputDetailsComponent implements OnChanges {
    @Input() readOnly;
    @Input() inputData: KeyValue[];
    @Input() serviceBrief: ServiceBrief;
    @Input() selectServiceList: ServiceBrief[];
    serviceInputsForm: FormGroup;
    atLeastOneRequiredFormGroup: FormGroup;
    requiredFormGroup: FormGroup;
    serviceInputList: ServiceInput[] = [];
    @Output() passServiceInputForm = new EventEmitter<any>();
    @Output() passSelectedService = new EventEmitter<ServiceBrief>();
    @Output() passWarningMessage = new EventEmitter<any>();
    displayDateRange = false;
    startDateInput;
    endDateInput;
    displayTimeRange = false;
    startTimeInput;
    endTimeInput;
    inputType = INPUT_TYPE;
    invalidEndDateError = false;
    valueLengthInputsArray = [1, 2, 3, 5, 11, 13, 14, 15];

    constructor(private translateService: TranslateService) {
        this.initForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.serviceInputList = [];
        this.getListOfAllInputIdsPerService();
        this.setFormControlsAndValidators();
        this.setInputValues();
    }

    onServiceChange(event) {
        // on change, set selected service
        this.serviceBrief = event.value;
        this.passSelectedService.next(event.value);
        this.displayDateRange = false;
        this.serviceInputList = [];
        this.getListOfAllInputIdsPerService();
        this.setFormControlsAndValidators();
    }

    private initForm() {
        this.atLeastOneRequiredFormGroup = new FormGroup({});
        this.requiredFormGroup = new FormGroup({});
        this.serviceInputsForm = new FormGroup({
            requiredFormGroup: this.requiredFormGroup,
            atLeastOneRequiredFormGroup: this.atLeastOneRequiredFormGroup
        });
    }

    getListOfAllInputIdsPerService() {
        // get list of input ids for each service
        const getServiceInputsList = ServiceInputs.GET_SERVICE_INPUTS_LIST[this.serviceBrief?.id];
        if (getServiceInputsList) {
            getServiceInputsList.forEach(id => {
                this.serviceInputList.push(ServiceInputFormConfig.GET_INPUT_FORM_DETAILS[id]);
            });
        }
    }

    setFormControlsAndValidators() {
        this.resetForm();
        this.serviceInputList.forEach((input: ServiceInput) => {
            const control: FormControl = new FormControl('', input.validators);
            // if input is required, store in separate form
            const index = input.validators.findIndex(e => e === Validators.required);
            this.resetHiddenKey(input);

            if (index >= 0) {
                this.requiredFormGroup.addControl(input.controlName, control);
                input['required'] = true;
            } else {
                this.atLeastOneRequiredFormGroup.addControl(input.controlName, control);
                this.addInputValidatorsAndSubscribeToInputValueChange(input);
            }
        });

        this.serviceInputsForm = new FormGroup({
            requiredFormGroup: this.requiredFormGroup,
            atLeastOneRequiredFormGroup: this.atLeastOneRequiredFormGroup
        });
        this.handleWarningMessage();
        this.handleStartAndEndDateInputs();
        this.handleStartAndEndTimeInputs();
        this.passServiceInputFormToParent();
    }

    addInputValidatorsAndSubscribeToInputValueChange(input: ServiceInput) {
        if (!this.readOnly) {
            // is not readonly, but an input
            this.atLeastOneRequiredFormGroup.addValidators(CustomValidators.atLeastOneInputHasValueValidator);
            this.subscribeToInputValueChange(input.controlName, input);
        }
    }

    passServiceInputFormToParent() {
        if (!this.readOnly) {
            this.passServiceInputForm.emit(this.serviceInputsForm);
        }
    }

    resetForm() {
        if (!this.readOnly) {
            this.atLeastOneRequiredFormGroup.reset();
            this.requiredFormGroup.reset();
            this.initForm();
        }
    }

    setInputValues() {
        if (this.readOnly) {
            this.serviceInputList.forEach((element: ServiceInput) => {
                const controlNameAsKey = element.controlName.replace(ServiceInputFormConfig.FORM_CONTROL, '');
                const controlName = element.controlName;
                const index = this.inputData.findIndex(v => v.key === controlNameAsKey);

                if (index !== -1) {
                    if (element.required) {
                        const inputValue =
                            element.inputType === INPUT_TYPE.DATE_RANGE ? moment(this.inputData[index].value).format('') : this.inputData[index].value;
                        this.requiredFormGroup.get(controlName).setValue(inputValue);
                        this.clearValidators(controlName, this.requiredFormGroup);
                    } else {
                        this.atLeastOneRequiredFormGroup.get(controlName).setValue(this.inputData[index].value);
                        this.clearValidators(controlName, this.atLeastOneRequiredFormGroup);
                    }

                    this.hideEmptyInputs(element, this.inputData[index].value);
                }
            });
        }
    }

    clearValidators(controlName, form: FormGroup) {
        if (Object.keys(form.controls).length) {
            if (form.get(controlName)) {
                form.get(controlName).clearValidators();
            }
        }
    }

    hideEmptyInputs(element: ServiceInput, value) {
        if (value === '') {
            element.hidden = true;
        }
    }

    resetHiddenKey(input) {
        if (input['hidden']) {
            delete input['hidden'];
        }
    }

    subscribeToInputValueChange(controlName, input: ServiceInput) {
        this.atLeastOneRequiredFormGroup.controls[controlName].valueChanges.subscribe(data => this.onInputFormValueChange(controlName, input));
    }

    onInputFormValueChange(controlName, input: ServiceInput) {
        this.handleError(controlName, input);
    }

    handleError(controlName, input: ServiceInput) {
        const inputHasError = this.atLeastOneRequiredFormGroup.controls[controlName].errors ? true : false;
        if (inputHasError) {
            const errorMessage = input.errorMessage;
            if (this.atLeastOneRequiredFormGroup.controls[controlName].hasError('whiteSpace')) {
                input.errorMessage = 'validations.noWhiteSpacesOnly';
                input['hasError'] = true;
            } else if (this.atLeastOneRequiredFormGroup.controls[controlName].hasError('whitespaceBetween')) {
                input.errorMessage = 'validations.noWhiteSpaces';
                input['hasError'] = true;
            } else if (this.atLeastOneRequiredFormGroup.controls[controlName].hasError('specialchar')) {
                input.errorMessage = 'validations.noSpecialCharsOnly';
                input['hasError'] = true;
            } else if (this.atLeastOneRequiredFormGroup.controls[controlName].hasError('englishOrArabicLetters')) {
                input.errorMessage = 'validations.noLettersOnly';
                input['hasError'] = true;
            }
            else if (this.atLeastOneRequiredFormGroup.controls[controlName].hasError('englishOrArabicLettersOnly')) {
                input.errorMessage = 'validations.LettersOnly';
                input['hasError'] = true;
            }
            else if (this.atLeastOneRequiredFormGroup.controls[controlName].hasError('numbersAndSpecialChars')) {
                input.errorMessage = 'validations.noNumbersAndSpecialCharsOnly';
                input['hasError'] = true;
            } else if (this.atLeastOneRequiredFormGroup.controls[controlName].hasError('lettersAndSpecialChars')) {
                input.errorMessage = 'validations.noLettersAndSpecialCharsOnly';
                input['hasError'] = true;
                } 
                else if (this.atLeastOneRequiredFormGroup.controls[controlName].hasError('numbersAndAndHyphen')) {
                    input.errorMessage = 'validations.numbersAndAndHyphen';
                    input['hasError'] = true;
                    }
                else {
                input.errorMessage = errorMessage;
                input['hasError'] = true;
            }
        } else {
            input['hasError'] = false;
        }
    }

    onKeyDown(event, inputItem: ServiceInput) {
        if (inputItem.inputType === INPUT_TYPE.NUMBER) {
            if (ServiceInputFormConfig.INPUT_INVALID_CHARS.NUMBER_INPUT.includes(event.key)) {
                event.preventDefault();
            }
        }
    }

    handleStartAndEndDateInputs() {
        this.startDateInput = this.serviceInputList.find(v => v.id === SERVICE_INPUT_ID.START_DATE);
        this.endDateInput = this.serviceInputList.find(v => v.id === SERVICE_INPUT_ID.END_DATE);
        this.displayDateRange = this.startDateInput && this.endDateInput ? true : false;
    }

    handleStartAndEndTimeInputs() {
        this.startTimeInput = this.serviceInputList.find(v => v.id === SERVICE_INPUT_ID.START_TIME);
        this.endTimeInput = this.serviceInputList.find(v => v.id === SERVICE_INPUT_ID.END_TIME);
        this.displayTimeRange = this.startTimeInput && this.endTimeInput ? true : false;
    }

    handleWarningMessage() {
        const fieldNames = this.serviceInputList.filter(i => !i.required).map(i => this.translateService.instant(i.labelName));

        fieldNames.push(this.translateService.instant('addImageAttachments'));
        const last = fieldNames.pop();
        const message = fieldNames.join('، ') + ' ' + this.translateService.instant('or') + ' ' + last;
        this.passWarningMessage.emit(message);
    }
}
